import {useMutation} from '@tanstack/react-query';
import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import {api} from 'app/modules/Remote';
import {ApiSuggestedLayer} from 'app/modules/Remote/api';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {LoggedInUserActions} from 'app/providers/AuthProvider';

import {useManageFeatures} from './ManageFeaturesProvider';
import ManageFeaturesView from './ManageFeaturesView';
import ManagePropertiesAgentPrompt from './ManagePropertiesAgentPrompt';
import {DrawPropertiesState, useApplyFeatureEdit} from './ManagePropertiesDraw';
import ManagePropertiesSuggestedLayers from './ManagePropertiesSuggestedLayers';

// Stable identity, so an empty result doesn't invalidate the panel's memo every render.
const NO_SUGGESTIONS: ApiSuggestedLayer[] = [];

export interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  loggedInUserActions: LoggedInUserActions;
}

const ManagePropertiesAgent: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  organization,
  profile,
  loggedInUserActions,
}) => {
  const {manageFeaturesState, manageFeaturesActions, loading} = useManageFeatures();

  const [featureToEdit, setFeatureToEdit] = React.useState<null | geojson.Feature>(null);
  const [editMode, setEditMode] = React.useState<boolean>(false);

  const {setUploadedFeatureCollection, setProblems, setFeatureCollectionErrors} =
    manageFeaturesActions;

  // No nameNewFeature: agent mode only edits generated features, it can't draw new ones.
  useApplyFeatureEdit(featureToEdit, setUploadedFeatureCollection);

  // Suggestions come from their own endpoint so they can land while generation, which runs for
  // minutes, is still going. Layers the user already added survive a regeneration — only the
  // suggestion list is replaced. Unwrapped here so the panel gets one stable array per result
  // rather than a fresh one on every render.
  const suggestLayers = useMutation({
    mutationFn: async (prompt: string) => {
      const response = await api.agent.suggestLayers(prompt, organization.get('id'));
      return (response.toJS().data as {suggestions: ApiSuggestedLayer[]}).suggestions;
    },
  });

  // Clear rather than overwrite, so the provider drops the state it keys by
  // feature id — dismissed rows, split outlines — and an open edit session can't
  // write its geometry onto the next generation's feature. Generated ids restart
  // at 1 every time, so any of that state left behind would land on a new site.
  // Errors from a previous submit go too, or they'd resurface under the new results.
  const clearGeneratedFeatures = React.useCallback(
    (prompt: string) => {
      setFeatureToEdit(null);
      setEditMode(false);
      setUploadedFeatureCollection(null);
      setProblems(null);
      setFeatureCollectionErrors(null);
      suggestLayers.mutate(prompt);
    },
    [setUploadedFeatureCollection, setProblems, setFeatureCollectionErrors, suggestLayers.mutate]
  );

  const drawPropertiesState: DrawPropertiesState = {
    editMode: editMode,
    setEditMode: setEditMode,
    featureToEdit: featureToEdit,
    setFeatureToEdit: setFeatureToEdit,
  };

  return (
    <ManageFeaturesView
      profile={profile}
      organization={organization}
      manageFeaturesState={manageFeaturesState}
      manageFeaturesActions={manageFeaturesActions}
      loggedInUserActions={loggedInUserActions}
      mode={'agentUpload'}
      drawPropertiesState={drawPropertiesState}
      loading={loading}
      firebaseToken={null}
      agentPrompt={
        <ManagePropertiesAgentPrompt
          setUploadedFeatureCollection={setUploadedFeatureCollection}
          onGenerateStart={clearGeneratedFeatures}
        />
      }
      suggestedLayers={
        <ManagePropertiesSuggestedLayers
          organization={organization}
          // react-query drops data when a new prompt starts, so the row never shows suggestions
          // that describe the previous one.
          suggestions={suggestLayers.data ?? NO_SUGGESTIONS}
          loading={suggestLayers.isPending}
          selectedLayerKeys={manageFeaturesState.selectedLayerKeys}
          setSelectedLayerKeys={manageFeaturesActions.setSelectedLayerKeys}
        />
      }
    />
  );
};

export default ManagePropertiesAgent;
